import { ArrowRight, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { api, isApiError } from '../../../api';
import { Section } from '../../../components/blocks';
import { DataError } from '../../../components/DataError';
import { EntityRef } from '../../../components/EntityRef';
import { KeyValue } from '../../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../../components/PageHeader';
import { CopyText, Loading } from '../../../components/States';
import { useRetained } from '../../../components/Write';
import { Button } from '../../../components/ui/button';
import { formatDateTime } from '../../../lib/format';
import { useApi } from '../../../lib/useApi';
import { EnumBadge, useEnumLabel } from '../ui';
import { DOSSIER } from '../../domains/status';
import { Payload, ReceiptsTable, SYNC_STATUS, SyncErrorCode, TX_STATUS, VNNIC_BASE } from './shared';

/**
 * `/compliance/vnnic/sync-transactions/:id`: one attempt at sending a registrant dossier to
 * VNNIC, with the answer recorded for it. The registrant's own data is only visible inside the
 * submitted form, and only to staff allowed to see it.
 */
export function VnnicSyncTransactionPage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const label = useEnumLabel();
  const { id = '' } = useParams();
  const result = useApi((signal) => api.getVnnicSyncTransaction(id, { signal }), [id]);
  const tx = useRetained(result.status === 'success' ? result.data : undefined);
  useDocumentTitle(tx ? `${t('syncTx.title')} ${tx.domain_name}` : t('syncTx.title'));
  const crumbs = [
    { label: t('crumbs.compliance') },
    { label: t('crumbs.vnnic') },
    { label: t('vnnic.sync.title'), to: `${VNNIC_BASE}/dossier-sync` },
    { label: tx?.domain_name ?? '…' },
  ];

  if (!tx) {
    return (
      <>
        <PageHeader title={t('syncTx.title')} crumbs={crumbs} />
        {result.status === 'error' ? (
          <DataError
            error={result.error}
            onRetry={isApiError(result.error) && result.error.status === 404 ? undefined : result.reload}
            title={t('syncTx.loadError')}
          />
        ) : (
          <Loading rows={4} />
        )}
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('syncTx.heading', { attempt: tx.attempt })}
        crumbs={crumbs}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <EnumBadge group="txStatus" value={tx.status} tones={TX_STATUS} />
            <span>{label('syncOperation', tx.operation)}</span>
            <span aria-hidden="true">·</span>
            <EntityRef label={tx.unicode_name || tx.domain_name} to={`/domains/${encodeURIComponent(tx.domain_name)}`} id={tx.domain_id} />
          </span>
        }
        actions={
          <>
            <Button asChild size="sm" variant="ghost">
              <Link to={`${VNNIC_BASE}/dossier-sync?dossier=${encodeURIComponent(tx.dossier_id)}`}>
                {t('syncTx.allSubmissions')} <ArrowRight />
              </Link>
            </Button>
            <Button size="sm" variant="outline" onClick={result.reload}>
              <RefreshCw /> {tc('action.refresh')}
            </Button>
          </>
        }
      />
      <div className="space-y-4">
        <Section title={t('syncTx.facts')}>
          <KeyValue
            columns={3}
            rows={[
              [
                t('syncTx.domain'),
                <EntityRef key="d" label={tx.unicode_name || tx.domain_name} to={`/domains/${encodeURIComponent(tx.domain_name)}`} id={tx.domain_id} />,
              ],
              [
                t('syncTx.dossier'),
                <span key="dos" className="flex flex-wrap items-center gap-1.5">
                  {t('syncTx.dossierValue', { version: tx.dossier_version })}
                  <EnumBadge group="dossierStatus" value={tx.dossier_status} tones={DOSSIER} />
                </span>,
              ],
              [t('syncTx.syncStatus'), <EnumBadge key="sy" group="syncStatus" value={tx.sync_status} tones={SYNC_STATUS} />],
              [t('syncTx.status'), <EnumBadge key="s" group="txStatus" value={tx.status} tones={TX_STATUS} />],
              [t('syncTx.operation'), label('syncOperation', tx.operation)],
              [t('syncTx.attempt'), tx.attempt],
              [t('syncTx.channel'), <code key="a">{tx.adapter}</code>],
              [t('syncTx.schema'), <code key="v">{tx.schema_version}</code>],
              [t('syncTx.submitted'), formatDateTime(tx.submitted_at)],
              [t('syncTx.completed'), formatDateTime(tx.completed_at)],
              [t('syncTx.reference'), tx.external_reference ? <code key="r">{tx.external_reference}</code> : null],
              tx.error_code || tx.error_message
                ? [
                    t('syncTx.error'),
                    <span key="e">
                      {tx.error_code ? <SyncErrorCode code={tx.error_code} /> : null} {tx.error_message}
                    </span>,
                  ]
                : false,
              [t('syncTx.id'), <CopyText key="id" value={tx.id} />],
            ]}
          />
        </Section>
        <Section title={t('syncTx.receipts')}>
          <ReceiptsTable receipts={tx.receipts} statusMap={TX_STATUS} />
        </Section>
        <Section title={t('syncTx.payloads')}>
          <div className="grid gap-2">
            <Payload label={t('vnnic.sync.tx.requestPayload')} value={tx.request_payload} />
            <Payload label={t('vnnic.sync.tx.responsePayload')} value={tx.response_payload} />
          </div>
        </Section>
      </div>
    </>
  );
}
